import React, { useCallback } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import {
  Icon,
  Text,
  Box,
  HStack,
  VStack,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
} from '@televet/kibble-ui';
import { useSettingPageDetail } from '../hooks/useSettingPageDetail';
import { SettingsSmsEvents } from 'shared/providers/TrackingProvider/EventNames';
import { SettingsRoutes } from 'routes';
import { useTracking } from 'shared/providers/TrackingProvider';
import RecapFlowUpgradeBanner from 'shared/components/UpgradeBanners/RecapFlowUpgradeBanner';
import { useModularity } from 'shared/hooks/useModularity';

const SettingsNavigation = ({ isMobile }: { isMobile: boolean }): JSX.Element => {
  const { settingsSection } = useSettingPageDetail();
  const { trackEvent } = useTracking();
  const { url } = useRouteMatch();
  const { isRecapClinic } = useModularity();

  const trackSettingsNavigationEventInGA = useCallback(
    (pathname: string): void => {
      let event: SettingsSmsEvents | undefined = undefined;
      switch (pathname) {
        case SettingsRoutes.ClinicPhone:
          event = SettingsSmsEvents.View;
          break;
      }
      if (event) {
        trackEvent(event);
      }
    },
    [trackEvent],
  );

  return (
    <Flex
      className="SettingsNavigation"
      direction="column"
      align="stretch"
      flex="1 1 auto"
      {...(!isMobile && { borderRightWidth: '1px', borderRightColor: 'border.default' })}
    >
      <VStack
        {...(!isMobile && {
          minW: '234px',
          maxW: '250px',
        })}
        flex="1 1 auto"
        w="100%"
        align="flex-start"
        overflowY="auto"
      >
        {settingsSection.map((settingSection, index) => {
          const visibleSections = settingSection.sectionOption.filter((section) => section.isVisible);
          if (!visibleSections.length) {
            return null;
          }
          return (
            <Accordion
              allowMultiple
              defaultIndex={[0, 1, 2]}
              pl={2}
              pr={2}
              alignItems="flex-start"
              width="100%"
              key={`${settingSection.sectionName}-${index}`}
              mt="0px !important"
            >
              <AccordionItem border={'none'}>
                {({ isExpanded }): JSX.Element => (
                  <Box mt={3} mb={isExpanded ? 3 : 0}>
                    <AccordionButton
                      justifyContent="space-between"
                      height={8}
                      paddingInline={0}
                      pl={3}
                      pr={3}
                      borderRadius="md"
                      _focus={{ borderBox: 'none' }}
                      _expanded={{ mb: 1 }}
                    >
                      <Text size="sm" fontWeight="semibold">
                        {settingSection.sectionName}
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel alignItems="flex-start" ml={3} mr={3} paddingInline={0} padding={0}>
                      {settingSection.sectionOption.map((page) => {
                        if (page.isVisible) {
                          return (
                            <Box
                              onClick={(): void => trackSettingsNavigationEventInGA(page.relativePath)}
                              as={NavLink}
                              to={`${url}${page.relativePath}`}
                              id={page.id}
                              py={1}
                              px={2}
                              mb={1}
                              key={page.label}
                              bg="transparent"
                              display="block"
                              color="text.subtle"
                              _hover={{
                                bg: 'background.primaryAlpha.hover',
                              }}
                              _activeLink={{
                                bg: 'background.primaryAlpha',
                                color: 'text.brand',
                              }}
                            >
                              <HStack spacing={3}>
                                {page.leftIcon && <Icon name={page.leftIcon} size="md" />}
                                <Text flexGrow={1} ml={3} color="inherit">
                                  {page.label}
                                </Text>
                                {page.rightIcon && <Icon size="md" name={page.rightIcon} variant="danger" />}
                              </HStack>
                            </Box>
                          );
                        }
                      })}
                    </AccordionPanel>
                  </Box>
                )}
              </AccordionItem>
            </Accordion>
          );
        })}
      </VStack>
      {isRecapClinic && (
        <Box p={5}>
          <RecapFlowUpgradeBanner />
        </Box>
      )}
    </Flex>
  );
};

export default SettingsNavigation;
